
import { supabase } from '@/integrations/supabase/client';
import { Message, Conversation } from '@/types';
import { formatMessages } from '../utils';
import { toast } from '@/hooks/use-toast';

/**
 * Fetches messages for a conversation
 */
export async function fetchMessagesService(conversationId: string, userId: string): Promise<{
  messages: Message[];
  conversation: Conversation | null;
}> {
  try {
    console.log("Fetching messages for conversation:", conversationId);
    
    // ✨ ORDENAÇÃO ESTÁVEL: created_at com fallback client_ts via metadata
    const { data: messagesData, error: messagesError } = await supabase
      .from('messages')
      .select('*')
      .eq('conversation_id', conversationId)
      .order('created_at', { ascending: true });
    
    // Reordenar no cliente com estabilidade via client_msg_id
    if (messagesData) {
      messagesData.sort((a, b) => {
        const aMetadata = (a.metadata || {}) as Record<string, any>;
        const bMetadata = (b.metadata || {}) as Record<string, any>;
        
        const aTime = new Date(a.created_at).getTime() || (aMetadata.client_ts ?? 0);
        const bTime = new Date(b.created_at).getTime() || (bMetadata.client_ts ?? 0);
        
        if (aTime !== bTime) return aTime - bTime;
        
        // Desempate estável por client_msg_id
        const aId = aMetadata.client_msg_id || a.id || '';
        const bId = bMetadata.client_msg_id || b.id || '';
        return aId.localeCompare(bId);
      });
    }
    
    if (messagesError) {
      console.error("Error fetching messages:", messagesError);
      toast({
        title: "Erro",
        description: "Falha ao carregar as mensagens. Por favor, tente novamente.",
        variant: "destructive",
      });
      return { messages: [], conversation: null };
    }
    
    const fileIds = messagesData
      ?.filter(msg => msg.file_id)
      .map(msg => msg.file_id);
      
    let filesData: any[] = [];
    
    if (fileIds && fileIds.length > 0) {
      const { data: files, error: filesError } = await supabase
        .from('files')
        .select('*')
        .in('id', fileIds);
        
      if (filesError) {
        console.error("Error fetching files:", filesError);
      } else {
        filesData = files || [];
      }
    }
    
    const formattedMessages = formatMessages(messagesData || [], filesData);
    console.log("Messages loaded:", formattedMessages);
    
    const { data: conversationData, error: conversationError } = await supabase
      .from('conversations')
      .select('*')
      .eq('id', conversationId)
      .single();
    
    if (conversationError) {
      console.error("Error fetching conversation:", conversationError);
      return { messages: formattedMessages, conversation: null };
    }
    
    console.log("Conversation loaded:", conversationData);
    return { 
      messages: formattedMessages, 
      conversation: conversationData ? {
        ...conversationData,
        metadata: conversationData.metadata as Conversation['metadata']
      } : null
    };
  } catch (error) {
    console.error("Exception in fetchMessages:", error);
    toast({
      title: "Erro",
      description: "Falha ao carregar mensagens da conversa. Por favor, tente novamente.",
      variant: "destructive",
    });
    return { messages: [], conversation: null };
  }
}

/**
 * Saves a message to the database with robust error handling and retry logic
 */
export async function saveMessageService(message: Partial<Message>, userId: string): Promise<{ id: string } | null> {
  const maxRetries = 3;
  let retryCount = 0;
  
  // 🔒 BLOQUEIO CIRÚRGICO: Assistant messages MUST use RPC only
  if (message.role === 'assistant') {
    console.error('❌ [CHAT] Assistant messages must be saved via RPC only (saveGeneralMessageRPC)');
    throw new Error('[CHAT] Assistant messages must be saved via RPC only');
  }
  
  while (retryCount < maxRetries) {
    try {
      console.log(`💾 Tentativa ${retryCount + 1} de salvar mensagem para usuário:`, userId);
      console.log("📝 Dados da mensagem:", {
        conversation_id: message.conversation_id,
        role: message.role,
        content: message.content?.substring(0, 50) + "...",
        file_id: message.file?.id
      });
      
      const messageId = message.id || `${Date.now()}-${Math.random().toString(36).substr(2, 9)}-${crypto.randomUUID()}`;
      const messageTimestamp = message.created_at || new Date().toISOString();
      
      // ✅ CORREÇÃO: Não salvar mensagens vazias (exceto placeholders de streaming)
      if (!message.content?.trim() && !message.metadata?.isStreaming) {
        console.log("❌ Bloqueado salvamento de mensagem vazia");
        return null;
      }
      
      const { error } = await supabase
        .from('messages')
        .insert({
          id: messageId,
          conversation_id: message.conversation_id,
          user_id: userId,
          role: message.role,
          content: message.content,
          file_id: message.file?.id,
          created_at: messageTimestamp,
          // ⚖️ CORREÇÃO CIRÚRGICA: Incluir metadata para persistir ABNT
          metadata: message.metadata || {},
        });
        
      if (error) {
        console.error(`❌ Erro na tentativa ${retryCount + 1} de salvar mensagem:`, error);
        console.error("📊 Código do erro:", error.code, "| Mensagem:", error.message);
        
        // Erro de permissão RLS - não retenta
        if (error.code === 'PGRST301' || error.code === '42501' || error.message.includes('permission')) {
          console.error("🚫 Erro de permissão RLS detectado, não tentando novamente");
          toast({
            title: "Erro de Permissão",
            description: "Você não tem permissão para salvar mensagens nesta conversa.",
            variant: "destructive",
          });
          return null;
        }
        
        // Erro de foreign key (conversa não encontrada) - retenta com delay
        if (error.code === '23503' || error.message.includes('foreign key') || error.message.includes('conversation')) {
          console.warn("⚠️ Conversa não encontrada (race condition), tentando novamente...");
          retryCount++;
          if (retryCount < maxRetries) {
            // Delay maior no primeiro retry para dar tempo da conversa propagar
            const delay = retryCount === 1 ? 500 : retryCount * 1000;
            console.log(`🔄 Aguardando ${delay}ms antes da próxima tentativa...`);
            await new Promise(resolve => setTimeout(resolve, delay));
            continue;
          } else {
            throw error;
          }
        }
        
        // Outros erros - retenta normalmente
        retryCount++;
        if (retryCount < maxRetries) {
          console.log(`🔄 Aguardando ${retryCount * 1000}ms antes da próxima tentativa...`);
          await new Promise(resolve => setTimeout(resolve, retryCount * 1000));
          continue;
        } else {
          throw error;
        }
      }
      
      console.log("✅ Mensagem salva com sucesso:", messageId);
      return { id: messageId };
      
    } catch (error) {
      console.error(`❌ Erro na tentativa ${retryCount + 1}:`, error);
      retryCount++;
      
      if (retryCount < maxRetries) {
        console.log(`🔄 Aguardando ${retryCount * 1000}ms antes da próxima tentativa...`);
        await new Promise(resolve => setTimeout(resolve, retryCount * 1000));
      } else {
        console.error("💥 Todas as tentativas falharam ao salvar mensagem");
        toast({
          title: "Erro",
          description: "Falha ao salvar sua mensagem após várias tentativas. Por favor, tente novamente.",
          variant: "destructive",
        });
        return null;
      }
    }
  }
  
  return null;
}

/**
 * Updates a message in the database
 */
export async function updateMessageService(messageId: string, updates: Partial<Message>, userId: string): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('messages')
      .update({
        content: updates.content,
        edited: updates.edited || false,
        edited_at: updates.edited_at || null,
        // ⚖️ CORREÇÃO CIRÚRGICA: Atualizar metadata se fornecido
        ...(updates.metadata && { metadata: updates.metadata }),
      })
      .eq('id', messageId)
      .eq('user_id', userId);
      
    if (error) {
      console.error("Error updating message:", error);
      toast({
        title: "Erro",
        description: "Falha ao atualizar a mensagem. Por favor, tente novamente.",
        variant: "destructive",
      });
      return false;
    }
    
    return true;
  } catch (error) {
    console.error("Error updating message:", error);
    return false;
  }
}
